import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Tooltip } from 'react-tooltip';
import Layout from '../components/common/Layout';
import { apiService } from '../services/api';
import { notifications } from '../utils/notifications';
import '../styles/print-balance-sheet.css';

const FinancialPractice: React.FC = () => {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();

  // State variables for API data
  const [balanceSheet, setBalanceSheet] = useState<any>(null);
  const [progress, setProgress] = useState<any>(null);
  const [currentScenario, setCurrentScenario] = useState<any>(null);
  const [sessionData, setSessionData] = useState<any>(null);
  const [assetOptions, setAssetOptions] = useState<any[]>([]);
  const [ownershipOptions, setOwnershipOptions] = useState<any[]>([]);
  const [isComplete, setIsComplete] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Form state
  const [q1CategoryId, setQ1CategoryId] = useState<number | null>(null);
  const [q1Amount, setQ1Amount] = useState<string>('');
  const [q1NoImpact, setQ1NoImpact] = useState(false);
  const [q1Error, setQ1Error] = useState(false);
  const [q1Correct, setQ1Correct] = useState<boolean | null>(null);

  const [q2CategoryId, setQ2CategoryId] = useState<number | null>(null);
  const [q2Amount, setQ2Amount] = useState<string>('');
  const [q2NoImpact, setQ2NoImpact] = useState(false);
  const [q2Error, setQ2Error] = useState(false);
  const [q2Correct, setQ2Correct] = useState<boolean | null>(null);

  // Additional category state for Q1 and Q2 - now supporting unlimited categories
  const [q1AdditionalCategories, setQ1AdditionalCategories] = useState<Array<{categoryId: number | null, amount: string}>>([]);
  const [q2AdditionalCategories, setQ2AdditionalCategories] = useState<Array<{categoryId: number | null, amount: string}>>([]);

  // Track if answers have been submitted for this scenario
  const [hasSubmittedAnswers, setHasSubmittedAnswers] = useState(false);
  
  // Track if current scenario has existing transactions (for navigation)
  const [hasExistingTransactions, setHasExistingTransactions] = useState(false);

  // Mock balance sheet data matching current display
  const mockBalanceSheetData = {
    assets: [
      { name: 'Cash', transactions: [], balance: 0 },
      { name: 'Accounts Receivable', transactions: [], balance: 0 },
      { name: 'Inventory', transactions: [], balance: 0 },
      { name: 'Equipment', transactions: [], balance: 0 },
      { name: 'Furniture', transactions: [], balance: 0 },
      { name: 'Building', transactions: [], balance: 0 },
      { name: 'Land', transactions: [], balance: 0 },
    ],
    liabilities: [
      { name: 'Accounts Payable', transactions: [], balance: 0 },
      { name: 'Accrued Payables', transactions: [], balance: 0 },
      { name: 'Long-Term Debt', transactions: [], balance: 0 },
    ],
    equity: [
      { name: 'Common Stock', transactions: [], balance: 0 },
      { name: 'Retained Earnings', transactions: [], balance: 500 },
    ],
    totals: { assets: 0, liabilities: 0, equity: 0 }
  };

  // Helper function to format amounts
  const formatAmount = (amount: number | string) => {
    const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
    if (isNaN(numAmount) || numAmount === 0) return '0';
    const formatted = Math.abs(numAmount).toLocaleString();
    return numAmount < 0 ? `(${formatted})` : formatted;
  };

  // Parse amount: unsigned defaults to positive, negative sign = decrease
  const parseAmount = (input: string) => {
    const num = parseFloat(input.replace(/,/g, ''));
    return { amount: Math.abs(num) || 0, isDecrease: num < 0 };
  };

  // Format input with commas
  const formatInputAmount = (value: string) => {
    const num = parseFloat(value.replace(/,/g, ''));
    if (isNaN(num)) return value;
    const sign = value.startsWith('-') ? '-' : '';
    return sign + Math.abs(num).toLocaleString();
  };

  // Reset form after successful submission
  const resetForm = () => {
    setQ1CategoryId(null);
    setQ1Amount('');
    setQ1NoImpact(false);
    setQ1Error(false);
    setQ1Correct(null);
    setQ1AdditionalCategories([]);
    setQ2CategoryId(null);
    setQ2Amount('');
    setQ2NoImpact(false);
    setQ2Error(false);
    setQ2Correct(null);
    setQ2AdditionalCategories([]);
    setHasSubmittedAnswers(false);
  };

  // Calculate totals for balance summary
  const getQuestionTotals = () => {
    const q1Total = q1NoImpact || !q1Amount ? 0 : parseAmount(q1Amount).amount * (parseAmount(q1Amount).isDecrease ? -1 : 1);
    const q1AdditionalTotal = q1AdditionalCategories.reduce((sum, category) => {
      if (!category.amount) return sum;
      const parsed = parseAmount(category.amount);
      return sum + (parsed.amount * (parsed.isDecrease ? -1 : 1));
    }, 0);
    
    const q2Total = q2NoImpact || !q2Amount ? 0 : parseAmount(q2Amount).amount * (parseAmount(q2Amount).isDecrease ? -1 : 1);
    const q2AdditionalTotal = q2AdditionalCategories.reduce((sum, category) => {
      if (!category.amount) return sum;
      const parsed = parseAmount(category.amount);
      return sum + (parsed.amount * (parsed.isDecrease ? -1 : 1));
    }, 0);
    
    const totalQ1 = q1Total + q1AdditionalTotal;
    const totalQ2 = q2Total + q2AdditionalTotal;
    
    return { q1Total: totalQ1, q2Total: totalQ2, difference: totalQ1 - totalQ2 };
  };

  // Check if form is valid for submission
  const isFormValid = () => {
    const hasQ1Data = q1NoImpact || (q1CategoryId && q1Amount && parseAmount(q1Amount).amount > 0);
    const hasQ1AdditionalValid = q1AdditionalCategories.every(category => 
      category.categoryId && category.amount && parseAmount(category.amount).amount > 0
    );
    
    const hasQ2Data = q2NoImpact || (q2CategoryId && q2Amount && parseAmount(q2Amount).amount > 0);
    const hasQ2AdditionalValid = q2AdditionalCategories.every(category => 
      category.categoryId && category.amount && parseAmount(category.amount).amount > 0
    );
    
    return hasQ1Data && hasQ1AdditionalValid && hasQ2Data && hasQ2AdditionalValid;
  };

  // Handle form submission
  const handleSubmit = async () => {
    if (!isFormValid()) return;

    setIsSubmitting(true);
    setHasSubmittedAnswers(true);

    try {
      const q1Answers = [];
      if (!q1NoImpact) {
        if (q1CategoryId && q1Amount) {
          q1Answers.push({ categoryId: q1CategoryId, ...parseAmount(q1Amount) });
        }
        // Add all additional categories for Q1
        q1AdditionalCategories.forEach(category => {
          if (category.categoryId && category.amount) {
            q1Answers.push({ categoryId: category.categoryId, ...parseAmount(category.amount) });
          }
        });
      }
      
      const q2Answers = [];
      if (!q2NoImpact) {
        if (q2CategoryId && q2Amount) {
          q2Answers.push({ categoryId: q2CategoryId, ...parseAmount(q2Amount) });
        }
        // Add all additional categories for Q2
        q2AdditionalCategories.forEach(category => {
          if (category.categoryId && category.amount) {
            q2Answers.push({ categoryId: category.categoryId, ...parseAmount(category.amount) });
          }
        });
      }
      
      const answers = {
        q1: q1Answers,
        q2: q2Answers
      };

      const response = await apiService.submitFinancialAnswers(answers, currentScenario.id);

      // Update correct/incorrect status based on API response
      setQ1Correct(response.hints?.q1Correct || false);
      setQ2Correct(response.hints?.q2Correct || false);
      setQ1Error(!response.hints?.q1Correct);
      setQ2Error(!response.hints?.q2Correct);

      if (response.isCorrect) {
        toast.success(response.message || 'Correct! Great job.');
        setBalanceSheet(response.balanceSheet);

        if (response.nextScenario) {
          setCurrentScenario(response.nextScenario);
          setProgress((prev: any) => ({ ...prev, current: prev.current + 1 }));
          setHasExistingTransactions(false); // New scenario won't have transactions
          resetForm();
        } else {
          setIsComplete(true);  // All scenarios done
        }

        loadSession();
      } else {
        toast.error(response.message || 'Incorrect answer. Please try again.');
      }
    } catch (error: any) {
      console.error('Failed to submit answers:', error);
      if (error?.message?.includes('404')) {
        toast.error('Session not found. Redirecting to dashboard...');
        navigate('/dashboard');
      } else {
        toast.error('Network error. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Helper function to get question background color based on submission status
  const getQuestionBackgroundClass = (isCorrect: boolean | null, hasError: boolean) => {
    if (!hasSubmittedAnswers) {
      // Not submitted yet - neutral/normal background
      return 'bg-gray-50 border-2 border-gray-300';
    }
    
    // Submitted - show result
    if (isCorrect === true) {
      return 'bg-green-100 border-2 border-green-500';
    } else if (isCorrect === false || hasError) {
      return 'bg-red-100 border-2 border-red-500';
    }
    
    // Fallback to neutral
    return 'bg-gray-50 border-2 border-gray-300';
  };

  // Clear errors when user modifies input
  const handleQ1Change = () => {
    if (q1Error) setQ1Error(false);
    if (hasSubmittedAnswers) {
      setHasSubmittedAnswers(false);
      setQ1Correct(null);
      setQ2Correct(null);
    }
  };

  const handleQ2Change = () => {
    if (q2Error) setQ2Error(false);
    if (hasSubmittedAnswers) {
      setHasSubmittedAnswers(false);
      setQ1Correct(null);
      setQ2Correct(null);
    }
  };

  // Add/remove additional category functions
  const addQ1Additional = () => {
    setQ1AdditionalCategories(prev => [...prev, { categoryId: null, amount: '' }]);
  };

  const removeQ1Additional = (index: number) => {
    setQ1AdditionalCategories(prev => prev.filter((_, i) => i !== index));
    handleQ1Change();
  };

  const updateQ1Additional = (index: number, field: 'categoryId' | 'amount', value: number | null | string) => {
    setQ1AdditionalCategories(prev => prev.map((category, i) => 
      i === index ? { ...category, [field]: value } : category
    ));
    handleQ1Change();
  };

  const addQ2Additional = () => {
    setQ2AdditionalCategories(prev => [...prev, { categoryId: null, amount: '' }]);
  };

  const removeQ2Additional = (index: number) => {
    setQ2AdditionalCategories(prev => prev.filter((_, i) => i !== index));
    handleQ2Change();
  };

  const updateQ2Additional = (index: number, field: 'categoryId' | 'amount', value: number | null | string) => {
    setQ2AdditionalCategories(prev => prev.map((category, i) => 
      i === index ? { ...category, [field]: value } : category
    ));
    handleQ2Change();
  };

  // Print balance sheet function
  const handlePrintBalanceSheet = () => {
    // Add print-specific class to body
    document.body.classList.add('printing-balance-sheet');
    
    // Use setTimeout to ensure class is applied before printing
    setTimeout(() => {
      window.print();
      
      // Remove print class after printing
      setTimeout(() => {
        document.body.classList.remove('printing-balance-sheet');
      }, 100);
    }, 100);
  };

  // Reset session function
  const handleResetSession = async () => {
    const confirmed = await notifications.confirm({
      title: 'Reset Practice Session?',
      message: 'This will reset your progress back to scenario 1 and delete all your transaction entries. This action cannot be undone.',
      confirmText: 'Yes, Reset',
      cancelText: 'Cancel',
      type: 'warning',
      dangerMode: true
    });

    if (!confirmed) return;

    try {
      setIsLoading(true);
      const response = await apiService.resetFinancialExerciseSession(sessionData.session.id);
      
      // Show success message
      toast.success('Session reset successfully!');
      
      // Reload session data to refresh everything
      await loadSession();
    } catch (error) {
      console.error('Failed to reset session:', error);
      toast.error('Failed to reset session');
    } finally {
      setIsLoading(false);
    }
  };

  // Navigation functions for scenarios
  const goToPreviousScenario = async () => {
    if (!currentScenario || currentScenario.scenarioNumber <= 1) return;
    
    try {
      setIsLoading(true);
      const response = await apiService.navigateToScenario(currentScenario.scenarioNumber - 1);
      
      // Update current scenario and check if it has existing transactions
      setCurrentScenario(response.scenario);
      setBalanceSheet(response.balanceSheet);
      setHasExistingTransactions(response.hasExistingTransactions);
      setProgress(response.progress); // Update progress with new data
      
      // Reset form state
      resetForm();
    } catch (error) {
      console.error('Failed to go to previous scenario:', error);
      toast.error('Failed to load previous scenario');
    } finally {
      setIsLoading(false);
    }
  };

  const goToNextScenario = async () => {
    if (!currentScenario) return;
    
    try {
      setIsLoading(true);
      const response = await apiService.navigateToScenario(currentScenario.scenarioNumber + 1);
      
      // Update current scenario and check if it has existing transactions
      setCurrentScenario(response.scenario);
      setBalanceSheet(response.balanceSheet);
      setHasExistingTransactions(response.hasExistingTransactions);
      setProgress(response.progress); // Update progress with new data
      
      // Reset form state
      resetForm();
    } catch (error) {
      console.error('Failed to go to next scenario:', error);
      toast.error('Failed to load next scenario');
    } finally {
      setIsLoading(false);
    }
  };

  // Helper function to render category with multiple transaction rows
  const renderCategoryWithTransactions = (category: any) => {
    const rows = [];
    const transactionsPerRow = 3;
    const maxRows = 5;
    const totalTransactions = category.transactions.length;
    
    // For Cash and Retained Earnings, always show all rows (even empty ones)
    // For other categories, calculate actual rows needed (don't render empty rows)
    const shouldShowAllRows = category.name === 'Cash' || category.name === 'Retained Earnings';
    const rowsNeeded = Math.ceil(totalTransactions / transactionsPerRow);
    const actualRows = shouldShowAllRows ? maxRows : Math.min(rowsNeeded, maxRows);
    
    for (let rowIndex = 0; rowIndex < actualRows; rowIndex++) {
      const startIndex = rowIndex * transactionsPerRow;
      const endIndex = Math.min(startIndex + transactionsPerRow, totalTransactions);
      const rowTransactions = category.transactions.slice(startIndex, endIndex);
      
      rows.push(
        <tr key={`${category.name}-${rowIndex}`} className="border-b border-gray-300">
          <td className="py-2 px-2 font-medium">{rowIndex === 0 ? category.name : ''}</td>
          {Array.from({ length: 3 }).map((_, colIndex) => {
            const transactionIndex = startIndex + colIndex;
            const hasTransaction = transactionIndex < totalTransactions;
            const transaction = hasTransaction ? category.transactions[transactionIndex] : null;
            const amount = transaction ? (transaction.isDecrease ? -parseFloat(transaction.amount) : parseFloat(transaction.amount)) : null;
            const scenarioTitle = transaction ? `Scenario ${transaction.scenarioNumber}: ${transaction.scenarioText}` : '';
            
            return (
              <td 
                key={colIndex} 
                className={`activity-cell py-2 px-1 text-right font-mono w-16 ${
                  amount && amount < 0 ? 'text-red-600' : ''
                }`}
                id={transactionIndex === totalTransactions ? 
                  (category.name === 'Cash' ? 'newCashActivity' : 
                   category.name === 'Retained Earnings' ? 'newREActivity' : undefined) : 
                  undefined}
              >
                <span 
                  data-tooltip-id={`scenario-tooltip`}
                  data-tooltip-content={scenarioTitle}
                  data-tooltip-place="top"
                  className="cursor-help"
                >
                  {amount !== null ? formatAmount(amount) : '\u00A0'}
                </span>
              </td>
            );
          })}
          <td className="balance-cell py-2 px-2 text-right font-mono font-bold bg-gray-100">
            {rowIndex === 0 ? `$${formatAmount(category.balance)}` : ''}
          </td>
        </tr>
      );
    }
    
    return rows;
  };

  // Render category row
  const renderCategoryRow = (category: any) => {
    return (
      <tr key={category.name} className="border-b border-gray-200">
        <td className="py-2 px-3 text-left font-medium text-gray-900">{category.name}</td>
        {/* Activity columns (up to 5 visible) */}
        {Array.from({ length: 5 }).map((_, index) => (
          <td key={index} className="py-2 px-3 text-right font-mono text-sm text-gray-600 w-16">
            {category.transactions[index] ? formatAmount(category.transactions[index]) : ''}
          </td>
        ))}
        <td className={`py-2 px-3 text-right font-mono font-bold text-sm bg-gray-100 ${
          category.balance < 0 ? 'text-red-600' : 'text-gray-900'
        }`}>
          {formatAmount(category.balance)}
        </td>
      </tr>
    );
  };

  // Render section total row
  const renderSectionTotal = (title: string, total: number, bgColor: string) => {
    return (
      <tr className={`${bgColor} border-t-2 border-gray-400`}>
        <td className="py-3 px-3 font-bold text-gray-900">Total: {title}</td>
        {Array.from({ length: 5 }).map((_, index) => (
          <td key={index} className="py-3 px-3"></td>
        ))}
        <td className={`py-3 px-3 text-right font-mono font-bold text-lg ${
          total < 0 ? 'text-red-600' : 'text-gray-900'
        }`}>
          {formatAmount(total)}
        </td>
      </tr>
    );
  };

  // Load session data function
  const loadSession = async () => {
    setIsLoading(true);
    try {
      // Session derived from auth user - creates if none exists
      const sessionData = await apiService.getFinancialSession();
      console.log('Session Data:', sessionData);

      const assetCategories = await apiService.getFinancialCategories('asset');
      console.log('Asset Categories:', assetCategories);

      const ownershipCategories = await apiService.getFinancialCategories('liability,equity');
      console.log('Ownership Categories:', ownershipCategories);

      setSessionData(sessionData);
      setBalanceSheet(sessionData.balanceSheet);
      setProgress(sessionData.progress);
      setCurrentScenario(sessionData.scenario);
      setAssetOptions(assetCategories.categories || []);
      setOwnershipOptions(ownershipCategories.categories || []);
      setIsComplete(sessionData.session?.isComplete || false);
      
      // Check if current scenario has existing transactions
      setHasExistingTransactions(sessionData.hasExistingTransactions || false);
    } catch (error) {
      console.error('Failed to load session data:', error);
      toast.error('Failed to load session data');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadSession();
    window.scrollTo(0, 0);
  }, []);

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-2 pt-8">
        {/* Header Card - matching EditTemplate styling */}
        <div className="bg-white rounded-lg shadow mb-8" style={{ height: '65px' }}>
          <div className="flex items-center justify-between h-full px-6">
            {/* Left Side - Breadcrumb */}
            <div className="flex items-center space-x-2">
              {/* Back arrow and Dashboard link */}
              <button
                onClick={() => navigate('/dashboard')}
                className="text-blue-400 hover:text-blue-600 hover:underline font-medium text-sm flex items-center space-x-1 transition-all duration-200"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
                <span>Career Development Worksheets</span>
              </button>
              
              <span className="text-gray-400 text-sm">/</span>
              
              {/* Page Title */}
              <h1 className="text-lg font-bold text-gray-900">Financial Practice Session</h1>
            </div>

            {/* Right Side - Progress indicator */}
            <div className="flex items-center space-x-4">
              {isLoading ? (
                <div className="flex items-center space-x-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                  <span className="text-sm text-gray-600">Loading session...</span>
                </div>
              ) : isComplete ? (
                <div className="text-sm text-green-600">
                  <div className="scenario-number text-sm font-semibold text-green-600 mb-2">
                    Scenario {progress?.total || 16}/{progress?.total || 16} ✓
                  </div>
                  <div className="progress-bar bg-gray-200 h-2 rounded-full">
                    <div className="progress-fill bg-green-500 h-2 rounded-full" style={{ width: '100%' }}></div>
                  </div>
                </div>
              ) : (
                <div className="text-sm text-gray-600">
                  <div className="scenario-number text-sm font-semibold text-gray-600 mb-2">
                    Scenario {currentScenario?.scenarioNumber || 1} of {progress?.total || 16}
                  </div>
                  <div className="progress-bar bg-gray-200 h-2 rounded-full">
                    <div className="progress-fill bg-blue-500 h-2 rounded-full" style={{ width: `${progress?.percentComplete || 0}%` }}></div>
                  </div>
                </div>
              )}

              <button
              onClick={() => navigate('/financial/overview')}
              className="text-blue-400 hover:text-blue-600 hover:underline font-medium text-sm transition-all duration-200"
              >
              Overview
              </button>
            </div>
          </div>
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-2">
        {/* Two Column Layout */}
        <div className="grid grid-cols-12 gap-6">
          {/* Left Column - Balance Sheet (30%) */}
          <div className="col-span-4">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              <div className="transaction-dialog p-6">
                {isComplete ? (
                  /* Completion Message */
                  <div className="completion-section text-center">
                    <div className="completion-icon text-6xl mb-4">🎉</div>
                    <h2 className="text-2xl font-bold text-green-600 mb-4">Congratulations!</h2>
                    <p className="text-lg text-gray-700 mb-6">You have successfully completed all financial practice scenarios.</p>
                    
                    <div className="completion-stats bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
                      <div className="text-sm text-green-800">
                        <div className="flex justify-between mb-2">
                          <span>Scenarios Completed:</span>
                          <span className="font-bold">{progress?.total || 16}/{progress?.total || 16}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Progress:</span>
                          <span className="font-bold">100% ✓</span>
                        </div>
                      </div>
                    </div>

                    <div className="completion-actions">
                      <button 
                        className="btn btn-secondary px-6 py-3 text-sm font-medium text-green-700 bg-white border border-green-300 rounded-md hover:bg-green-50 focus:outline-none focus:ring-2 focus:ring-green-500 mb-4 w-full"
                        onClick={handlePrintBalanceSheet}
                      >
                        <svg className="w-4 h-4 inline mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                        </svg>
                        Print Balance Sheet
                      </button>
                      
                      <button 
                        className="btn btn-secondary px-6 py-3 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 mb-4 w-full"
                        onClick={() => handleResetSession()}
                        disabled={isLoading}
                      >
                        🔄 Reset Progress & Start Over
                      </button>
                      
                      <button 
                        className="btn btn-primary px-6 py-3 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 w-full"
                        onClick={() => navigate('/dashboard')}
                      >
                        📋 Return to Dashboard
                      </button>
                    </div>
                  </div>
                ) : (
                  /* Active Practice Session */
                  <>
                    {/* Scenario Text */}
                    <div className="scenario-text bg-blue-50 border-l-4 border-blue-400 p-4 mb-6 rounded-r">
                      📋 {currentScenario?.scenarioText || 'Loading scenario...'}
                    </div>
                    {/* QUESTION 1 */}
                    <div className={`question-section question-1 mb-6 rounded-lg p-3 ${getQuestionBackgroundClass(q1Correct, q1Error)}`}>
                      <div className="question-title text-md font-semibold text-gray-800 mb-4">❓ Question 1: Does it affect WHAT WE HAVE?</div>

                      {!q1NoImpact && (
                        <div className="category-input-row flex flex-col space-y-3 mb-4">
                          <div className="flex items-center space-x-3">
                            <label className="text-sm font-medium text-gray-700 w-20">Category:</label>
                            <select 
                              value={q1CategoryId || ''}
                              onChange={(e) => {
                                setQ1CategoryId(e.target.value ? parseInt(e.target.value) : null);
                                handleQ1Change();
                              }}
                              className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                            >
                              <option value="">Select category...</option>
                              {assetOptions.map(option => (
                                <option key={option.id} value={option.id}>{option.name}</option>
                              ))}
                            </select>
                            <input 
                              type="text" 
                              value={q1Amount}
                              onChange={(e) => {
                                setQ1Amount(e.target.value);
                                handleQ1Change();
                              }}
                              onBlur={(e) => {
                                if (e.target.value) {
                                  setQ1Amount(formatInputAmount(e.target.value));
                                }
                              }}
                              placeholder="+/- amount"
                              className="w-24 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm text-right font-mono"
                            />
                          </div>
                          
                          {/* Additional categories for Q1 */}
                          {q1AdditionalCategories.map((category, index) => (
                            <div key={index} className="flex items-center space-x-3 relative">
                              <label className="text-sm font-medium text-gray-700 w-20">Category:</label>
                              <select 
                                value={category.categoryId || ''}
                                onChange={(e) => {
                                  updateQ1Additional(index, 'categoryId', e.target.value ? parseInt(e.target.value) : null);
                                }}
                                className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                              >
                                <option value="">Select category...</option>
                                {assetOptions.map(option => (
                                  <option key={option.id} value={option.id}>{option.name}</option>
                                ))}
                              </select>
                              <input 
                                type="text" 
                                value={category.amount}
                                onChange={(e) => {
                                  updateQ1Additional(index, 'amount', e.target.value);
                                }}
                                onBlur={(e) => {
                                  if (e.target.value) {
                                    updateQ1Additional(index, 'amount', formatInputAmount(e.target.value));
                                  }
                                }}
                                placeholder="+/- amount"
                                className="w-24 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm text-right font-mono"
                              />
                              <button
                                onClick={() => removeQ1Additional(index)}
                                className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 focus:outline-none"
                                type="button"
                              >
                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                              </button>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Add another category button for Q1 */}
                      {!q1NoImpact && (
                        <div className="mb-4">
                          <button
                            onClick={addQ1Additional}
                            className="flex items-center space-x-1 text-blue-600 border border-blue-600 rounded-md px-3 py-1 text-sm hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
                            type="button"
                          >
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                            </svg>
                            <span>Add another category</span>
                          </button>
                        </div>
                      )}

                      <div className="no-impact-checkbox">
                        <label className="flex items-center text-sm text-gray-600">
                          <input 
                            type="checkbox" 
                            checked={q1NoImpact}
                            onChange={(e) => {
                              setQ1NoImpact(e.target.checked);
                              if (e.target.checked) {
                                setQ1CategoryId(null);
                                setQ1Amount('');
                                setQ1AdditionalCategories([]);
                              }
                              handleQ1Change();
                            }}
                            className="mr-2 rounded" 
                          />
                          No impact on WHAT WE HAVE
                        </label>
                      </div>
                    </div>

                    {/* QUESTION 2 */}
                    <div className={`question-section question-2 mb-6 rounded-lg p-3 ${getQuestionBackgroundClass(q2Correct, q2Error)}`}>
                      <div className="question-title text-md font-semibold text-gray-800 mb-4">❓ Question 2: Does it affect WHO OWNS IT?</div>

                      {!q2NoImpact && (
                        <div className="category-input-row flex flex-col space-y-3 mb-4">
                          <div className="flex items-center space-x-3">
                            <label className="text-sm font-medium text-gray-700 w-20">Category:</label>
                            <select 
                              value={q2CategoryId || ''}
                              onChange={(e) => {
                                setQ2CategoryId(e.target.value ? parseInt(e.target.value) : null);
                                handleQ2Change();
                              }}
                              className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                            >
                              <option value="">Select category...</option>
                              {ownershipOptions.map(option => (
                                <option key={option.id} value={option.id}>{option.name}</option>
                              ))}
                            </select>
                            <input 
                              type="text" 
                              value={q2Amount}
                              onChange={(e) => {
                                setQ2Amount(e.target.value);
                                handleQ2Change();
                              }}
                              onBlur={(e) => {
                                if (e.target.value) {
                                  setQ2Amount(formatInputAmount(e.target.value));
                                }
                              }}
                              placeholder="+/- amount"
                              className="w-24 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm text-right font-mono"
                            />
                          </div>
                          
                          {/* Additional categories for Q2 */}
                          {q2AdditionalCategories.map((category, index) => (
                            <div key={index} className="flex items-center space-x-3 relative">
                              <label className="text-sm font-medium text-gray-700 w-20">Category:</label>
                              <select 
                                value={category.categoryId || ''}
                                onChange={(e) => {
                                  updateQ2Additional(index, 'categoryId', e.target.value ? parseInt(e.target.value) : null);
                                }}
                                className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                              >
                                <option value="">Select category...</option>
                                {ownershipOptions.map(option => (
                                  <option key={option.id} value={option.id}>{option.name}</option>
                                ))}
                              </select>
                              <input 
                                type="text" 
                                value={category.amount}
                                onChange={(e) => {
                                  updateQ2Additional(index, 'amount', e.target.value);
                                }}
                                onBlur={(e) => {
                                  if (e.target.value) {
                                    updateQ2Additional(index, 'amount', formatInputAmount(e.target.value));
                                  }
                                }}
                                placeholder="+/- amount"
                                className="w-24 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm text-right font-mono"
                              />
                              <button
                                onClick={() => removeQ2Additional(index)}
                                className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 focus:outline-none"
                                type="button"
                              >
                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                              </button>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Add another category button for Q2 */}
                      {!q2NoImpact && (
                        <div className="mb-4">
                          <button
                            onClick={addQ2Additional}
                            className="flex items-center space-x-1 text-blue-600 border border-blue-600 rounded-md px-3 py-1 text-sm hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
                            type="button"
                          >
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                            </svg>
                            <span>Add another category</span>
                          </button>
                        </div>
                      )}

                      <div className="no-impact-checkbox">
                        <label className="flex items-center text-sm text-gray-600">
                          <input 
                            type="checkbox" 
                            checked={q2NoImpact}
                            onChange={(e) => {
                              setQ2NoImpact(e.target.checked);
                              if (e.target.checked) {
                                setQ2CategoryId(null);
                                setQ2Amount('');
                                setQ2AdditionalCategories([]);
                              }
                              handleQ2Change();
                            }}
                            className="mr-2 rounded" 
                          />
                          No impact on WHO OWNS IT
                        </label>
                      </div>
                    </div>

                    {/* BALANCE SUMMARY */}
                    <div className="balance-summary bg-gray-50 rounded-lg p-4 mb-6">
                      {(() => {
                        const { q1Total, q2Total, difference } = getQuestionTotals();
                        return (
                          <>
                            <div className="balance-summary-row flex justify-between text-sm mb-1">
                              <span>Question 1 Total:</span>
                              <span className={`font-mono ${q1Total < 0 ? 'text-red-600' : 'text-green-600'}`}>
                                {q1Total === 0 ? '$0' : q1Total < 0 ? `($${formatAmount(Math.abs(q1Total))})` : `$${formatAmount(q1Total)}`}
                              </span>
                            </div>
                            <div className="balance-summary-row flex justify-between text-sm mb-1">
                              <span>Question 2 Total:</span>
                              <span className={`font-mono ${q2Total < 0 ? 'text-red-600' : 'text-green-600'}`}>
                                {q2Total === 0 ? '$0' : q2Total < 0 ? `($${formatAmount(Math.abs(q2Total))})` : `$${formatAmount(q2Total)}`}
                              </span>
                            </div>
                            <div className="balance-summary-row total flex justify-between text-sm font-bold border-t pt-2">
                              <span>Difference:</span>
                              <span className={`font-mono ${difference === 0 ? 'text-green-600' : 'text-red-600'}`}>
                                {difference === 0 ? '$0 ✓' : difference < 0 ? `($${formatAmount(Math.abs(difference))})` : `$${formatAmount(difference)}`}
                              </span>
                            </div>
                          </>
                        );
                      })()}
                    </div>

                    {/* DIALOG ACTIONS */}
                    <div className="dialog-actions flex flex-col md:flex-row md:justify-between gap-3 pt-4 border-t">
                      <div className="flex flex-col md:flex-row gap-2 md:gap-3 order-2 md:order-1">
                        <button 
                          className="btn btn-secondary px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed w-full md:w-auto"
                          id="backBtn"
                          onClick={goToPreviousScenario}
                          disabled={!currentScenario || currentScenario.scenarioNumber <= 1 || isLoading}
                        >
                          <span className="hidden 2xl:inline">← </span>Previous Scenario
                        </button>
                        <button 
                          className="btn btn-secondary px-3 py-2 text-sm font-medium text-red-700 bg-white border border-red-300 rounded-md hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed w-full md:w-auto"
                          onClick={handleResetSession}
                          disabled={isLoading}
                        >
                          <span className="hidden 2xl:inline">🔄 </span>Reset Session
                        </button>
                      </div>
                      <div className="flex flex-col md:flex-row gap-2 md:gap-3 order-1 md:order-2">
                        {/* Show Next button if current scenario has existing transactions */}
                        {hasExistingTransactions ? (
                          <button 
                            className="btn btn-primary px-3 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed w-full md:w-auto"
                            id="nextBtn"
                            onClick={goToNextScenario}
                            disabled={isLoading}
                          >
                            Next Scenario<span className="hidden 2xl:inline"> →</span>
                          </button>
                        ) : (
                          /* Show Submit button if current scenario doesn't have existing transactions */
                          <button 
                            onClick={handleSubmit}
                            disabled={!isFormValid() || isSubmitting}
                            className={`btn btn-primary px-3 py-2 text-sm font-medium border border-transparent rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 w-full md:w-auto ${
                              !isFormValid() || isSubmitting
                                ? 'text-gray-400 bg-gray-300 cursor-not-allowed'
                                : 'text-white bg-blue-600 hover:bg-blue-700'
                            }`}
                            id="submitBtn"
                          >
                            {isSubmitting ? 'Submitting...' : 'Submit Answer'}
                          </button>
                        )}
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Right Column - Transaction Dialog (70%) */}
          <div className="col-span-8">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden print-balance-sheet-container">
              {isLoading && (
                <div className="flex items-center justify-center h-96 bg-gray-50">
                  <div className="flex flex-col items-center space-y-4">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                    <p className="text-gray-600 text-sm">Loading balance sheet...</p>
                  </div>
                </div>
              )}
              {!isLoading && (

              <div className="balance-sheet p-4">
                <div className="balance-sheet-grid grid grid-cols-2 gap-4 h-full">
                  {/* WHAT WE HAVE (Left side, spans 2 rows) */}
                  <div className="bs-section section-have left-side row-span-2 border-2 border-black flex flex-col">
                    <div className="section-header bg-green-200 p-3 text-center font-bold border-b-2 border-black" style={{ backgroundColor: '#C6EFCE' }}>WHAT WE HAVE</div>
                    <div className="flex-1 flex flex-col">
                      <table className="category-table w-full text-xs">
                        <thead>
                          <tr className="border-b border-gray-300 bg-gray-100">
                            <th className="p-2 text-left">Category</th>
                            <th className="p-1 text-center" colSpan={3}>Activity (Amounts)</th>
                            <th className="p-2 text-center bg-gray-100">Balance</th>
                          </tr>
                        </thead>
                        <tbody>
                          {/* Render all asset categories */}
                          {balanceSheet?.categories?.filter((cat: any) => cat.type === 'asset')?.sort((a: any, b: any) => a.displayOrder - b.displayOrder)?.map((asset: any, index: number) => {
                            if (asset.name === 'Cash') {
                              return renderCategoryWithTransactions(asset);
                            } else {
                              return (
                                <tr key={asset.name} className="border-b border-gray-300">
                                  <td className="py-2 px-2 font-medium">{asset.name}</td>
                                  {Array.from({ length: 3 }).map((_, colIndex) => {
                                    const transaction = asset.transactions[colIndex];
                                    const amount = transaction ? (transaction.isDecrease ? -parseFloat(transaction.amount) : parseFloat(transaction.amount)) : null;
                                    return (
                                      <td key={colIndex} className="activity-cell py-2 px-1 text-right font-mono w-16">
                                      {amount !== null ? formatAmount(amount) : '\u00A0'}
                                      </td>
                                    );
                                  })}
                                  <td className="balance-cell py-2 px-2 text-right font-mono font-bold bg-gray-100">
                                    ${formatAmount(typeof asset.balance === 'string' ? parseFloat(asset.balance) : asset.balance)}
                                  </td>
                                </tr>
                              );
                            }
                          })}
                        </tbody>
                      </table>
                      {/* Total Row - positioned at bottom */}
                      <table className="category-table w-full text-xs mt-auto">
                        <tbody>
                          <tr className="section-total bg-green-200 border-t-2 border-green-600">
                            <td className="p-2 font-bold" colSpan={4}>Total: WHAT WE HAVE</td>
                            <td className="balance-cell p-2 text-right font-mono font-bold text-lg bg-gray-100">${formatAmount(balanceSheet.totals.assets)}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* WHAT SOMEONE ELSE OWNS (Top right) */}
                  <div className="bs-section section-someone border-2 border-black">
                    <div className="section-header bg-pink-200 p-3 text-center font-bold border-b-2 border-black" style={{ backgroundColor: '#FFC7CE' }}>WHAT SOMEONE ELSE OWNS</div>
                    <table className="category-table w-full text-xs">
                      <thead>
                        <tr className="border-b border-gray-300 bg-gray-100">
                          <th className="p-2 text-left">Category</th>
                          <th className="p-1 text-center" colSpan={3}>Activity (Amounts)</th>
                          <th className="p-2 text-center bg-gray-100">Balance</th>
                        </tr>
                      </thead>
                      <tbody>
                        {/* Render all liability categories */}
                        {balanceSheet?.categories?.filter((cat: any) => cat.type === 'liability')?.sort((a: any, b: any) => a.displayOrder - b.displayOrder)?.map((liability: any) => (
                          <tr key={liability.name} className="border-b border-gray-300">
                            <td className="py-2 px-2 font-medium">{liability.name}</td>
                            {Array.from({ length: 3 }).map((_, colIndex) => {
                              const amount = liability.transactions[colIndex];
                              const transaction = liability.transactions[colIndex];
                              const scenarioTitle = transaction ? `Scenario ${transaction.scenarioNumber}: ${transaction.scenarioText}` : '';
                              return (
                                <td 
                                  key={colIndex} 
                                  className={`activity-cell py-2 px-1 text-right font-mono w-16 ${
                                    amount && amount.amount < 0 ? 'text-red-600' : ''
                                  }`}
                                >
                                  <span 
                                    data-tooltip-id={`scenario-tooltip`}
                                    data-tooltip-content={scenarioTitle}
                                    data-tooltip-place="top"
                                    className="cursor-help"
                                  >
                                    {amount ? formatAmount(amount.amount) : '\u00A0'}
                                  </span>
                                </td>
                              );
                            })}
                            <td className="balance-cell py-2 px-2 text-right font-mono font-bold bg-gray-100">
                              ${formatAmount(liability.balance)}
                            </td>
                          </tr>
                        ))}
                        <tr className="section-total bg-pink-200 border-t-2 border-pink-600">
                          <td className="p-2 font-bold" colSpan={4}>Total: WHAT SOMEONE ELSE OWNS</td>
                          <td className="balance-cell p-2 text-right font-mono font-bold text-lg bg-gray-100">${formatAmount(balanceSheet.totals.liabilities)}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  {/* WHAT WE OWN (Bottom right) */}
                  <div className="bs-section section-own border-2 border-black">
                    <div className="section-header bg-blue-200 p-3 text-center font-bold border-b-2 border-black" style={{ backgroundColor: '#DAEEF3' }}>WHAT WE OWN</div>
                    <table className="category-table w-full text-xs">
                      <thead>
                        <tr className="border-b border-gray-300 bg-gray-100">
                          <th className="p-2 text-left">Category</th>
                          <th className="p-1 text-center" colSpan={3}>Activity (Amounts)</th>
                          <th className="p-2 text-center bg-gray-100">Balance</th>
                        </tr>
                      </thead>
                      <tbody>
                        {/* Render all equity categories */}
                        {balanceSheet?.categories?.filter((cat: any) => cat.type === 'equity')?.sort((a: any, b: any) => a.displayOrder - b.displayOrder)?.map((equity: any) => {
                          if (equity.name === 'Retained Earnings') {
                            return renderCategoryWithTransactions(equity);
                          } else {
                            return (
                              <tr key={equity.name} className="border-b border-gray-300">
                                <td className="py-2 px-2 font-medium">{equity.name}</td>
                                {Array.from({ length: 3 }).map((_, colIndex) => {
                                  const amount = equity.transactions[colIndex];
                                  const transaction = equity.transactions[colIndex];
                                  const scenarioTitle = transaction ? `Scenario ${transaction.scenarioNumber}: ${transaction.scenarioText}` : '';
                                  return (
                                    <td 
                                      key={colIndex} 
                                      className={`activity-cell py-2 px-1 text-right font-mono w-16 ${
                                        amount && amount.amount < 0 ? 'text-red-600' : ''
                                      }`}
                                    >
                                      <span 
                                        data-tooltip-id={`scenario-tooltip`}
                                        data-tooltip-content={scenarioTitle}
                                        data-tooltip-place="top"
                                        className="cursor-help"
                                      >
                                        {amount ? formatAmount(amount.amount) : '\u00A0'}
                                      </span>
                                    </td>
                                  );
                                })}
                                <td className="balance-cell py-2 px-2 text-right font-mono font-bold bg-gray-100">
                                  ${formatAmount(equity.balance)}
                                </td>
                              </tr>
                            );
                          }
                        })}
                        <tr className="section-total bg-blue-200 border-t-2 border-blue-600">
                          <td className="p-2 font-bold" colSpan={4}>Total: WHAT WE OWN</td>
                          <td className="balance-cell p-2 text-right font-mono font-bold text-lg bg-gray-100">${formatAmount(balanceSheet.totals.equity)}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* GRAND TOTALS (Must be horizontally aligned) */}
                <div className="grand-totals bg-yellow-50 border-t-2 border-yellow-600 p-4 mt-4">
                  <div className="flex justify-between items-center">
                    <div className="left-total text-lg font-bold text-gray-900">Total: WHAT WE HAVE = ${formatAmount(balanceSheet?.totals?.assets || 0)}</div>
                    <div className="right-total text-lg font-bold text-gray-900">Total: SOMEONE ELSE + WE OWN = ${formatAmount((balanceSheet?.totals?.liabilities || 0) + (balanceSheet?.totals?.equity || 0))}</div>
                  </div>
                </div>
              </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <Tooltip 
        id="scenario-tooltip" 
        style={{ 
          backgroundColor: '#374151', 
          color: 'white', 
          borderRadius: '6px',
          padding: '8px 12px',
          fontSize: '12px',
          maxWidth: '300px',
          textAlign: 'center',
          zIndex: 9999
        }}
        offset={10}
        delayShow={200}
      />
    </Layout>
  );
};

export default FinancialPractice;